import React, { useCallback, useEffect, useState } from 'react';
import { Badge, Button, Snackbar, Tooltip } from '@mui/material';
import PublishIcon from '@mui/icons-material/Publish';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { getTutorCanvasState, publishCanvas } from '../../services/gnPublishService';

interface PublishButtonProps {
  courseId: number;
  nodeId: number;
  /** Bump to re-check publish state (e.g. after an auto-save completes). */
  refreshKey?: number;
}

/**
 * The explicit Publish action (spec 046 FR-033/FR-036): saving is tutor-private; this
 * button snapshots the working canvas for students and shows the unpublished-changes
 * indicator (a badge) whenever the working copy is newer than the published one.
 */
const PublishButton: React.FC<PublishButtonProps> = ({ courseId, nodeId, refreshKey = 0 }) => {
  const [hasUnpublished, setHasUnpublished] = useState(false);
  const [everSaved, setEverSaved] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [snack, setSnack] = useState<string | null>(null);

  const refresh = useCallback(() => {
    getTutorCanvasState(courseId, nodeId)
      .then(state => {
        setEverSaved(Boolean(state?.blocks));
        setHasUnpublished(Boolean(state?.hasUnpublishedChanges));
      })
      .catch(() => {});
  }, [courseId, nodeId]);

  useEffect(() => {
    refresh();
  }, [refresh, refreshKey]);

  const handlePublish = async () => {
    setPublishing(true);
    try {
      await publishCanvas(courseId, nodeId);
      setHasUnpublished(false);
      setSnack('Published — students now see this version.');
    } catch {
      setSnack('Publish failed — please try again.');
    } finally {
      setPublishing(false);
    }
  };

  return (
    <>
      <Tooltip
        title={
          hasUnpublished
            ? 'You have unpublished changes — students see the last published version'
            : 'Students see the latest published version'
        }
      >
        <span>
          <Badge color="warning" variant="dot" invisible={!hasUnpublished}>
            <Button
              size="small"
              variant={hasUnpublished ? 'contained' : 'outlined'}
              startIcon={hasUnpublished ? <PublishIcon /> : <CheckCircleIcon />}
              onClick={handlePublish}
              disabled={publishing || !everSaved}
              data-testid="gn-publish-button"
            >
              {publishing ? 'Publishing…' : hasUnpublished ? 'Publish' : 'Published'}
            </Button>
          </Badge>
        </span>
      </Tooltip>
      <Snackbar
        open={Boolean(snack)}
        autoHideDuration={3000}
        onClose={() => setSnack(null)}
        message={snack}
      />
    </>
  );
};

export default PublishButton;
